import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import {
  CaretLeft, CaretRight, Printer, GearSix, ArrowsClockwise, Newspaper,
} from '@phosphor-icons/react';
import { plannerApi } from './plannerApi';
import { useAuth, canManagePlanner } from '@/auth/AuthContext';
import { fmtDateFull, ganttOrder, makeNameShortener } from './plannerConstants';
import { fetchShareQr } from './ganttPrint';
import GanttChart from './components/GanttChart';
import DigestSettingsDialog from './components/DigestSettingsDialog';
import { todayJst } from '@/utils/jstDate';

const HEALTH = {
  ok: { label: '順調', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  warning: { label: '要注意', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
  delayed: { label: '遅延', cls: 'bg-rose-50 text-rose-600 border-rose-200' },
  blocked: { label: 'ブロック中', cls: 'bg-rose-100 text-rose-800 border-rose-300' },
};
const TASK_STATUS_JA = { todo: '未着手', in_progress: '進行中', blocked: 'ブロック', done: '完了' };
const PRIO_JA = { urgent: '緊急', high: '高', medium: '中', low: '低' };

const taskColor = (t, todayStr) => {
  if (t.status === 'done') return '#059669';
  if (t.status === 'blocked') return '#9f1239';
  if (t.due_date && t.due_date < todayStr) return '#ea580c';
  if (t.status === 'in_progress') return '#2563EB';
  return '#a1a1aa';
};

const fmtJp = (s) => (s ? fmtDateFull(s) : '—');
const monday = (d) => { const x = new Date(d); x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); return x; };
const iso = (d) => d.toISOString().slice(0, 10);
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

// 印刷専用の軽量ガント — %ベースのバーでページ幅に収まる（画面では非表示）。
const DigestPrintGantt = ({ items, todayStr }) => {
  const toT = (s) => new Date(String(s).slice(0, 10) + 'T00:00:00').getTime();
  const dated = items.filter((it) => it.start && it.end);
  if (dated.length === 0) return null;
  const min = Math.min(...dated.map((it) => toT(it.start)));
  const max = Math.max(...dated.map((it) => toT(it.end)));
  const span = Math.max(max - min, 86400000);
  const pct = (t) => Math.min(100, Math.max(0, ((t - min) / span) * 100));
  const todayT = toT(todayStr);
  return (
    <div className="digest-print-gantt hidden" style={{ WebkitPrintColorAdjust: 'exact', printColorAdjust: 'exact' }}>
      <div className="flex justify-between text-[9px] text-zinc-500 mb-0.5">
        <span>{fmtJp(new Date(min).toISOString().slice(0, 10))}</span>
        <span>{fmtJp(new Date(max).toISOString().slice(0, 10))}</span>
      </div>
      <div className="border border-zinc-300 rounded">
        {dated.map((it) => (
          <div key={it.id} className="flex items-center border-b border-zinc-200 last:border-0" style={{ height: 18 }}>
            <div className="w-[26%] shrink-0 px-1.5 text-[9px] text-zinc-700 truncate border-r border-zinc-200">{it.label}</div>
            <div className="relative flex-1 h-full">
              {todayT >= min && todayT <= max && (
                <div className="absolute top-0 bottom-0 w-px bg-rose-400" style={{ left: `${pct(todayT)}%` }} />
              )}
              <div className="absolute rounded-sm" style={{
                left: `${pct(toT(it.start))}%`,
                width: `${Math.max(pct(toT(it.end)) - pct(toT(it.start)), 1.2)}%`,
                top: 4, bottom: 4, backgroundColor: it.color,
              }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


const memberLabel = (members, nameFn) => {
  const names = (members || []).map(nameFn);
  return names.length > 3 ? `${names.slice(0, 3).join('、')} 他${names.length - 3}名` : names.join('、');
};

const TaskLine = ({ t, nameFn }) => (
  <li className="flex items-baseline gap-2 text-[13px] text-zinc-700 min-w-0">
    <span className="flex-1 min-w-0 truncate">{t.title}</span>
    {(t.members || []).length > 0 && (
      <span className="text-[11px] text-zinc-400 truncate min-w-0 max-w-[45%] shrink" title={(t.members || []).join('、')}>
        {memberLabel(t.members, nameFn)}
      </span>
    )}
    {t.due_date && <span className="text-[11px] text-zinc-400 shrink-0 tabular-nums">期限 {fmtJp(t.due_date)}</span>}
  </li>
);

const Section = ({ title, tone, children }) => (
  <div className="digest-avoid-break">
    <div className={`text-[12px] font-bold mb-1 ${tone || 'text-zinc-600'}`}>{title}</div>
    {children}
  </div>
);

export default function PlannerDigest() {
  const { user } = useAuth();
  const canManage = canManagePlanner(user);
  const [weekStart, setWeekStart] = useState(() => iso(monday(new Date())));
  const [deptId, setDeptId] = useState('all');
  const [departments, setDepartments] = useState([]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [qrMap, setQrMap] = useState({});
  const [printing, setPrinting] = useState(false);
  const todayStr = todayJst();

  const weekEnd = iso(addDays(new Date(weekStart + 'T00:00:00'), 6));

  const load = useCallback(() => {
    setLoading(true);
    plannerApi.digest({ start: weekStart, department_id: deptId })
      .then(setData).catch(() => toast.error('ダイジェストの取得に失敗しました'))
      .finally(() => setLoading(false));
  }, [weekStart, deptId]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { plannerApi.departments().then(setDepartments).catch(() => {}); }, []);

  const nameFn = useMemo(() => {
    const all = (data?.projects || []).flatMap((p) => (p.tasks || []).flatMap((t) => t.members || []));
    return makeNameShortener(all);
  }, [data]);

  const s = data?.summary;

  // 印刷時: 各プロジェクトの公開共有QRを取得（未発行なら自動発行）してから印刷する。
  const handlePrint = async () => {
    const projects = data?.projects || [];
    setPrinting(true);
    try {
      const entries = await Promise.all(projects.map(async (p) => [p.id, await fetchShareQr('project', p.id)]));
      setQrMap(Object.fromEntries(entries.filter(([, v]) => v?.dataUrl)));
    } catch { /* QRなしでも印刷は続行 */ }
    setPrinting(false);
    setTimeout(() => window.print(), 200);
  };

  return (
    <div className="space-y-5" data-testid="planner-digest-page">
      {/* 印刷時はアプリ枠を隠し、ガントの代わりに一覧テーブルを出す */}
      <style>{`@media print {
        header, aside, .digest-no-print { display: none !important; }
        main { padding: 0 !important; }
        .digest-project-card { break-before: page; page-break-before: always; box-shadow: none !important; }
        .digest-avoid-break { break-inside: avoid; page-break-inside: avoid; }
        .digest-print-table tr { break-inside: avoid; page-break-inside: avoid; }
        .digest-print-table thead { display: table-header-group; }
        .digest-print-gantt > div > .flex { break-inside: avoid; page-break-inside: avoid; }
        .digest-screen-gantt { display: none !important; }
        .digest-print-gantt { display: block !important; }
        .digest-print-table { display: table !important; }
        .digest-print-qr { display: block !important; }
        body { background: #fff !important; }
      }`}</style>

      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-zinc-900 flex items-center gap-2">
            <Newspaper size={22} weight="duotone" className="text-indigo-500 digest-no-print" />
            週間プロジェクトダイジェスト
          </h1>
          <p className="text-xs text-zinc-500 mt-0.5">進行中プロジェクト、課題、翌週の重点事項をまとめて確認できます。</p>
          <p className="text-[11px] text-zinc-400 mt-0.5">
            対象期間: {fmtJp(weekStart)} ～ {fmtJp(weekEnd)} ／ 作成日: {fmtJp(todayStr)}
          </p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap digest-no-print">
          <div className="flex items-center rounded-lg border border-zinc-200 bg-white">
            <Button variant="ghost" size="sm" className="h-9 px-2" onClick={() => setWeekStart(iso(addDays(new Date(weekStart + 'T00:00:00'), -7)))} data-testid="digest-prev-week"><CaretLeft size={14} /></Button>
            <button type="button" className="text-xs font-semibold text-zinc-700 px-2 hover:text-indigo-600" onClick={() => setWeekStart(iso(monday(new Date())))} data-testid="digest-this-week">今週</button>
            <Button variant="ghost" size="sm" className="h-9 px-2" onClick={() => setWeekStart(iso(addDays(new Date(weekStart + 'T00:00:00'), 7)))} data-testid="digest-next-week"><CaretRight size={14} /></Button>
          </div>
          <Select value={deptId} onValueChange={setDeptId}>
            <SelectTrigger className="w-44 h-9" data-testid="digest-dept-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部署</SelectItem>
              {departments.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" className="h-9" onClick={load} disabled={loading} data-testid="digest-refresh-btn">
            <ArrowsClockwise size={14} className={loading ? 'animate-spin' : ''} /><span className="ml-1">更新</span>
          </Button>
          <Button variant="outline" className="h-9" onClick={handlePrint} disabled={printing} data-testid="digest-print-btn">
            <Printer size={14} className={printing ? 'animate-pulse' : ''} /><span className="ml-1">{printing ? 'QR取得中…' : '印刷 / PDF'}</span>
          </Button>
          {canManage && (
            <Button variant="outline" className="h-9" onClick={() => setSettingsOpen(true)} data-testid="digest-settings-btn">
              <GearSix size={14} /><span className="ml-1">ダイジェスト設定</span>
            </Button>
          )}
        </div>
      </div>

      {/* 全体サマリー */}
      {s && (
        <Card className="p-4" data-testid="digest-summary">
          <h2 className="text-sm font-bold text-zinc-700 mb-3">全体サマリー</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2 text-center">
            {[
              ['進行中', s.active, 'text-indigo-600'],
              ['順調', s.ok, 'text-emerald-600'],
              ['要注意', s.warning, 'text-amber-600'],
              ['遅延', s.delayed, 'text-rose-600'],
              ['ブロック中', s.blocked, 'text-rose-800'],
              ['今週完了タスク', s.done_this_week, 'text-emerald-600'],
              ['期限超過タスク', s.overdue_tasks, 'text-rose-600'],
              ['翌週期限タスク', s.due_next_week, 'text-amber-600'],
            ].map(([label, v, cls]) => (
              <div key={label} className="rounded-lg border border-zinc-100 py-2 px-1">
                <div className={`text-xl font-bold tabular-nums ${cls}`}>{v}</div>
                <div className="text-[10px] text-zinc-500">{label}</div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {loading && !data ? (
        <div className="py-16 text-center text-zinc-400 text-sm">読み込み中…</div>
      ) : (data?.projects || []).length === 0 ? (
        <Card className="p-10 text-center text-sm text-zinc-500" data-testid="digest-empty">今週対象の進行中プロジェクトはありません。</Card>
      ) : (
        data.projects.map((p) => {
          const h = HEALTH[p.health] || HEALTH.ok;
          const sec = p.sections;
          const ganttItems = [...(p.tasks || [])]
            .filter((t) => t.start_date || t.due_date)
            .sort(ganttOrder((t) => t.due_date || t.start_date, (t) => t.start_date || t.due_date))
            .map((t) => ({
              id: t.id, label: t.title,
              sub: [(t.members || []).map(nameFn).join('、'), TASK_STATUS_JA[t.status]].filter(Boolean).join(' ・ '),
              start: t.start_date || t.due_date, end: t.due_date || t.start_date,
              color: taskColor(t, todayStr), priority: t.priority,
            }));
          const risks = [
            ...sec.overdue.map((t) => ({ ...t, tag: '期限超過' })),
            ...sec.blocked.map((t) => ({ ...t, tag: 'ブロック' })),
            ...sec.no_owner.map((t) => ({ ...t, tag: '担当者未設定' })),
            ...sec.no_due.map((t) => ({ ...t, tag: '期限未設定' })),
          ];
          const nextWeek = [
            ...sec.due_next_week.map((t) => ({ ...t, tag: '翌週期限' })),
            ...sec.starting_next_week.map((t) => ({ ...t, tag: '翌週開始' })),
            ...sec.high_priority_open.map((t) => ({ ...t, tag: `優先度${PRIO_JA[t.priority] || ''}` })),
          ].filter((t, i, arr) => arr.findIndex((x) => x.id === t.id) === i);

          return (
            <Card key={p.id} className="p-4 space-y-3 digest-project-card" data-testid={`digest-project-${p.id}`}>
              <div className="flex items-start justify-between gap-2 flex-wrap border-b border-zinc-200 pb-2 digest-avoid-break">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="text-base font-bold text-zinc-800">{p.name}</h3>
                    <Badge variant="outline" className={h.cls} data-testid={`digest-health-${p.id}`}>{h.label}</Badge>
                  </div>
                  <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-2 flex-wrap">
                    {p.department_name && <span>{p.department_name}</span>}
                    {p.owner_name && <span>担当: {p.owner_name}</span>}
                    <span>期間: {fmtJp(p.start_date)} ～ {fmtJp(p.end_date)}</span>
                    {p.updated_at && <span>最終更新: {fmtJp(p.updated_at)}</span>}
                  </div>
                </div>
                <div className="flex items-start gap-3 shrink-0">
                  {p.progress != null && (
                    <div className="w-40">
                      <div className="flex justify-between text-[10px] text-zinc-500 mb-0.5"><span>進捗率</span><span className="tabular-nums font-semibold">{p.progress}%（{p.done_count}/{p.task_count}）</span></div>
                      <div className="h-2 rounded-full bg-zinc-100 overflow-hidden"><div className="h-full bg-indigo-500" style={{ width: `${p.progress}%` }} /></div>
                    </div>
                  )}
                  {/* 印刷専用 公開共有QR（画面では非表示） */}
                  {qrMap[p.id]?.dataUrl && (
                    <div className="digest-print-qr hidden text-center border border-zinc-200 rounded p-1 bg-white" data-testid={`digest-qr-${p.id}`}>
                      <img src={qrMap[p.id].dataUrl} alt="QR" style={{ width: '17mm', height: '17mm', display: 'block', margin: '0 auto' }} />
                      <div className="text-[7px] text-zinc-500 mt-0.5">スキャンで閲覧</div>
                      <div className="text-[6px] text-zinc-600 break-all leading-tight" style={{ maxWidth: '22mm' }}>{qrMap[p.id].url.replace(/^https?:\/\//, '')}</div>
                    </div>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                <Section title="今週の動き">
                  {sec.moved_this_week.length === 0
                    ? <p className="text-[12px] text-zinc-400">今週の更新はありません。</p>
                    : <p className="text-[13px] text-zinc-700">{sec.moved_this_week.length}件のサブタスクが更新されました（完了 {sec.completed_this_week.length}件）。</p>}
                </Section>
                <Section title="今週完了した項目" tone="text-emerald-700">
                  {sec.completed_this_week.length === 0
                    ? <p className="text-[12px] text-zinc-400">今週完了したサブタスクはありません。</p>
                    : <ul className="space-y-0.5">{sec.completed_this_week.map((t) => <TaskLine key={t.id} t={t} nameFn={nameFn} />)}</ul>}
                </Section>
                <Section title="現在進行中" tone="text-blue-700">
                  {sec.in_progress.length === 0
                    ? <p className="text-[12px] text-zinc-400">進行中のサブタスクはありません。</p>
                    : <ul className="space-y-0.5">{sec.in_progress.map((t) => <TaskLine key={t.id} t={t} nameFn={nameFn} />)}</ul>}
                </Section>
                <Section title="リスク / ブロッカー" tone="text-rose-700">
                  {risks.length === 0
                    ? <p className="text-[12px] text-zinc-400">現在確認されているブロッカーはありません。</p>
                    : (
                      <ul className="space-y-0.5">
                        {risks.map((t, i) => (
                          <li key={`${t.id}-${t.tag}-${i}`} className="flex items-baseline gap-2 text-[13px] text-zinc-700">
                            <Badge variant="outline" className="bg-rose-50 text-rose-600 border-rose-200 text-[9px] px-1 py-0 shrink-0">{t.tag}</Badge>
                            <span className="flex-1 min-w-0 truncate">{t.title}</span>
                            {t.due_date && <span className="text-[11px] text-zinc-400 shrink-0">{fmtJp(t.due_date)}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                </Section>
                <Section title="翌週の重点事項" tone="text-amber-700">
                  {nextWeek.length === 0
                    ? <p className="text-[12px] text-zinc-400">翌週の重点事項は特にありません。</p>
                    : (
                      <ul className="space-y-0.5">
                        {nextWeek.map((t) => (
                          <li key={t.id} className="flex items-baseline gap-2 text-[13px] text-zinc-700">
                            <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[9px] px-1 py-0 shrink-0">{t.tag}</Badge>
                            <span className="flex-1 min-w-0 truncate">{t.title}</span>
                            {t.due_date && <span className="text-[11px] text-zinc-400 shrink-0">{fmtJp(t.due_date)}</span>}
                          </li>
                        ))}
                      </ul>
                    )}
                </Section>
              </div>

              {/* サブタスク ガント（画面用） */}
              {(p.tasks || []).length === 0 ? (
                <p className="text-[12px] text-zinc-400">このプロジェクトにはサブタスクが登録されていません。</p>
              ) : ganttItems.length === 0 ? (
                <p className="text-[12px] text-zinc-400">サブタスクの日付情報が不足しているため、ガントチャートを表示できません。</p>
              ) : (
                <div className="digest-screen-gantt">
                  <GanttChart items={ganttItems} emptyText="" />
                </div>
              )}
              {ganttItems.length > 0 && <DigestPrintGantt items={ganttItems} todayStr={todayStr} />}

              {/* 印刷用 サブタスク一覧（画面では非表示） */}
              {(p.tasks || []).length > 0 && (
                <table className="digest-print-table hidden w-full text-[11px] border-collapse">
                  <thead>
                    <tr>
                      {['サブタスク名', 'ステータス', '担当者', '開始日', '期限日'].map((hd) => (
                        <th key={hd} className="border border-zinc-300 bg-zinc-100 px-2 py-1 text-left">{hd}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {p.tasks.map((t) => (
                      <tr key={t.id}>
                        <td className="border border-zinc-300 px-2 py-1">{t.title}</td>
                        <td className="border border-zinc-300 px-2 py-1">{TASK_STATUS_JA[t.status] || t.status}</td>
                        <td className="border border-zinc-300 px-2 py-1">{(t.members || []).map(nameFn).join('、')}</td>
                        <td className="border border-zinc-300 px-2 py-1">{fmtJp(t.start_date)}</td>
                        <td className="border border-zinc-300 px-2 py-1">{fmtJp(t.due_date)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </Card>
          );
        })
      )}

      <DigestSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} periodLabel={`${fmtJp(weekStart)} ～ ${fmtJp(weekEnd)}`} weekStart={weekStart} />
    </div>
  );
}
